import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { User, ChevronRight, Shield, AlertTriangle, AlertOctagon } from "lucide-react";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import AdminLayout from "./AdminLayout";
import axios from "axios";
import { API } from "../../App";
import { toast } from "sonner";

const AdminArchives = ({ user }) => {
  const [archives, setArchives] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const res = await axios.get(`${API}/admin/archives`, { withCredentials: true });
      setArchives(res.data);
    } catch (error) {
      toast.error("Ошибка загрузки");
    } finally {
      setLoading(false);
    }
  };

  // Calculate stats for each archive
  const getArchiveStats = (archive) => {
    const addresses = archive.addresses || [];
    const totalBalance = addresses.reduce((sum, a) => sum + (a.total_balance || 0), 0);
    const highRisk = addresses.filter(a => a.risk_level === 'high').length;
    const mediumRisk = addresses.filter(a => a.risk_level === 'medium').length;
    const lowRisk = addresses.filter(a => a.risk_level === 'low').length;
    return { totalBalance, highRisk, mediumRisk, lowRisk };
  };

  if (loading) {
    return (
      <AdminLayout user={user}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout user={user}>
      <div className="max-w-4xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-slate-900">Архивы</h1>
          <p className="text-slate-500">Выберите архив для просмотра и управления адресами</p>
        </div>

        <div className="grid gap-4">
          {archives.map((archive) => {
            const stats = getArchiveStats(archive);
            return (
              <Link key={archive.name} to={`/admin/archives/${archive.name}`}>
                <Card className="hover:shadow-lg transition-all hover:border-slate-300 cursor-pointer group">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-14 h-14 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg group-hover:scale-105 transition-transform">
                          <User className="w-7 h-7 text-white" />
                        </div>
                        <div>
                          <h2 className="text-xl font-bold text-slate-900 group-hover:text-indigo-600 transition-colors">
                            {archive.name}
                          </h2>
                          <p className="text-slate-500">
                            {archive.address_count} {archive.address_count === 1 ? 'адрес' : 
                              archive.address_count < 5 ? 'адреса' : 'адресов'}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-6">
                        {/* Stats */}
                        {archive.address_count > 0 && (
                          <div className="flex items-center gap-4 text-sm">
                            <div className="text-right">
                              <p className="text-slate-500">Общий баланс</p>
                              <p className="font-bold text-slate-900">${stats.totalBalance.toLocaleString()}</p>
                            </div>
                            <div className="flex items-center gap-2">
                              {stats.highRisk > 0 && (
                                <Badge className="bg-rose-100 text-rose-700 border-0">
                                  <AlertOctagon className="w-3 h-3 mr-1" />
                                  {stats.highRisk}
                                </Badge>
                              )}
                              {stats.mediumRisk > 0 && (
                                <Badge className="bg-amber-100 text-amber-700 border-0">
                                  <AlertTriangle className="w-3 h-3 mr-1" />
                                  {stats.mediumRisk}
                                </Badge>
                              )}
                              {stats.lowRisk > 0 && (
                                <Badge className="bg-emerald-100 text-emerald-700 border-0">
                                  <Shield className="w-3 h-3 mr-1" />
                                  {stats.lowRisk}
                                </Badge>
                              )}
                            </div>
                          </div>
                        )}
                        
                        <ChevronRight className="w-6 h-6 text-slate-400 group-hover:text-indigo-600 group-hover:translate-x-1 transition-all" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminArchives;
